/** Режимы правила: тестовый только пишет попадания, боевой вешает флаги. */
export const AutoHoldRuleMode = {
    Test: 0,
    Enforce: 1,
} as const;

/** Статусы кейса в очереди разбора. */
export const AutoHoldCaseStatus = {
    Pending: 0,
    Confirmed: 1,
    FalsePositive: 2,
    Released: 3,
} as const;

/** Откуда пришло попадание: живое событие, ручной прогон, бэкфилл. */
export const AutoHoldHitSource = {
    Live: 0,
    ManualPreview: 1,
    Backfill: 2,
} as const;

/** KPI автохолда за текущие сутки. */
export interface AutoHoldKpi {
    holds: number;
    confirmed: number;
    falsePositive: number;
    pending: number;
    blockedAmount: number;
}

/** Строка очереди разбора. */
export interface AutoHoldCaseRow {
    id: number;
    userId: number | string;
    status: number;
    triggeredRuleIds: number[];
    blockedAmount: number;
    evidenceJson: string;
    created: string;
    reviewedAt?: string;
    reviewedBy?: string;
    reviewComment?: string;
}

/** Строка правила с счётчиками попаданий. */
export interface AutoHoldRuleRow {
    id: number;
    name: string;
    enabled: boolean;
    mode: number;
    priority: number;
    actionFlags: number;
    rootJson: string;
    updatedBy?: string;
    updated: string;
    hits: number;
    confirmed: number;
    falsePositive: number;
}

/** Рычаг сигнала в конструкторе условия. */
export interface AutoHoldParamSchema {
    key: string;
    type: string;
    label: string;
    required: boolean;
    seedDefault?: string;
    description?: string;
}

/** Схема сигнала для селекта в конструкторе. */
export interface AutoHoldSignalSchema {
    id: string;
    displayName: string;
    description?: string;
    valueKind: number;
    unit: string;
    operators: number[];
    params: AutoHoldParamSchema[];
}

/** Сводка попаданий пользователя в правило. */
export interface AutoHoldHitRow {
    userId: number | string;
    lastMatchedAt: string;
    hitCount: number;
    explain: string;
    appliedFlags: number;
    appliedAt?: string;
    appliedBy?: string;
    mode: number;
    source: number;
    verdict?: number | null;
}

/** Строка результата ручного прогона. */
export interface AutoHoldPreviewRow {
    userId?: number | string;
    raw: string;
    valid: boolean;
    error?: string;
    matched: boolean;
    explain: string;
    appliedFlags: number;
}

/** Запись исключений: автохолд не трогает. */
export interface AutoHoldWhitelistRow {
    id: number;
    userId: number | string;
    reason: string;
    addedBy: string;
    created: string;
}

/** Справочник (страны, ASN, названия ключей). */
export interface AutoHoldDictionaryRow {
    key: string;
    values: string[];
    description?: string;
}

/** Узел дерева: группа И/ИЛИ или условие сигнала. */
export type AutoHoldAstNode = {
    op?: string;
    children?: AutoHoldAstNode[];
    signal?: string;
    cmp?: string;
    value?: unknown;
    params?: Record<string, string | number | boolean>;
};

/** Пустая группа «и» для новой политики. */
export function emptyAndGroup(): AutoHoldAstNode {
    return { op: 'And', children: [] };
}

/** Код оператора в AST (как на бэке). */
export const AUTO_HOLD_CMP_CODE: Record<number, string> = {
    0: 'Eq', 1: 'Neq', 2: 'Gte', 3: 'Lte', 4: 'Between', 5: 'In', 6: 'NotIn', 7: 'IsTrue', 8: 'IsFalse',
};

/** Подпись оператора в админке. */
export const AUTO_HOLD_CMP_LABEL: Record<string, string> = {
    Eq: 'равно',
    Neq: 'не равно',
    Gte: 'больше или равно',
    Lte: 'меньше или равно',
    Between: 'между',
    In: 'в списке',
    NotIn: 'не в списке',
    IsTrue: 'да',
    IsFalse: 'нет',
};

/** Код оператора по числу с бэка. */
export function autoHoldCmpCode(op: number | undefined): string {
    if (op === undefined) {
        return 'IsTrue';
    }
    return AUTO_HOLD_CMP_CODE[op] ?? 'IsTrue';
}

/** Русская подпись оператора. */
export function autoHoldCmpLabel(cmp: string | undefined): string {
    if (!cmp) {
        return '';
    }
    return AUTO_HOLD_CMP_LABEL[cmp] ?? cmp;
}

/** Заготовка условия по идентификатору сигнала. */
export function emptyCondition(signalId: string, schema?: AutoHoldSignalSchema): AutoHoldAstNode {
    const params: Record<string, string> = {};
    for (const p of schema?.params ?? []) {
        if (p.seedDefault != null && p.seedDefault !== '') {
            params[p.key] = p.seedDefault;
        }
    }
    return { signal: signalId, cmp: autoHoldCmpCode(schema?.operators?.[0]), params };
}
